// The pose catalog = bundled seed poses (docs/seed/poses.json, synced by
// scripts/sync-data.mjs) MERGED with admin-created "runtime" poses fetched from
// the API. It's a tiny external store so any component re-renders when the
// runtime poses load or change.
import { useSyncExternalStore } from 'react';
import raw from './poses.json';
import type { Pose } from '../types';

// Poses whose hold cue says to switch sides are two-sided ("2fers").
const BILATERAL_HOLD = /switch sides|each side|both sides|per side/i;
const withBilateral = (p: Pose): Pose => ({
  ...p,
  bilateral: p.bilateral ?? BILATERAL_HOLD.test(p.hold ?? ''),
});

export const seedPoses: Pose[] = (raw as Pose[]).map(withBilateral);

let runtimePoses: Pose[] = [];
let merged: Pose[] = compute();
const listeners = new Set<() => void>();

function compute(): Pose[] {
  const byId = new Map<string, Pose>();
  for (const p of seedPoses) byId.set(p.id, p);
  for (const p of runtimePoses) byId.set(p.id, withBilateral(p)); // admin poses win by id
  return [...byId.values()].sort((a, b) => a.order - b.order);
}

/** Merge in admin-created runtime poses (fetched from GET /public/poses). */
export function setRuntimePoses(list: Pose[]): void {
  runtimePoses = Array.isArray(list) ? list : [];
  merged = compute();
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
const getSnapshot = (): Pose[] => merged;

/** Reactive: the merged seed + runtime catalog, sorted by display order. */
export function usePoses(): Pose[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Reactive: distinct categories present in the catalog. */
export function useCategories(): string[] {
  const list = usePoses();
  return [...new Set(list.map((p) => p.category))].sort();
}

/** Non-reactive lookup — safe in handlers/effects; reads the latest merged list. */
export function getPose(id: string): Pose | undefined {
  return merged.find((p) => p.id === id);
}

/** The next display order that appends a new pose after everything else. */
export function nextPoseOrder(): number {
  return merged.reduce((max, p) => Math.max(max, p.order), 0) + 1;
}

/** Human label for a difficulty tier (1 → Beginner, 2 → Intermediate, 3 → Advanced). */
export function tierLabel(tier: number): string {
  return tier >= 3 ? 'Advanced' : tier === 2 ? 'Intermediate' : 'Beginner';
}
